import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { SpeechReviewContext } from "./SpeechSwallowReviewFormProvider";
import {
  getLatestSpeechReviewAPI,
  getSpeechInspectionAPI,
  getSpeechReviewAPI,
  speechReviewAPI,
  updateSpeechReviewAPI,
} from "../../../../services/speech.services";
import {
  Button,
  Col,
  Drawer,
  Form,
  Image,
  message,
  Modal,
  Row,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { LeftOutlined } from "@ant-design/icons";
import SpeechSwallowInitialFunctionalDiagnosis from "../../InspectionForms/FunctionalDiagnosis/SpeechSwallowInitialFunctionalDiagnosis";
import SpeechSwallowScalesReview from "../../InspectionSummaries/Scales/SpeechSwallowScalesReview";
import bedImg from "../../../../assets/images/patientIcons/bed.svg";
import useIsMobile from "../../../../hooks/useIsMobile";
import "../../ipforms.css";
import "../../Doctor/InitialInspection/InitialInspectionHeader.css";

const SpeechSwallowReviewForm = ({
  editId,
  visible,
  setVisible,
  refreshPage,
  iAssessment,
  addMode,
  setAddMode,
  setActiveSegment,
  reviewData,
  setDailyReviewRefresh,
  dailyRefresh,
  segmentData,
  patientData,
}) => {
  const isMobile = useIsMobile();
  const formBodyRef = useRef(null);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const [diagnosisForm] = Form.useForm();
  const [scalesForm] = Form.useForm();
  const { formData, setFormData } = useContext(SpeechReviewContext);
  const [activeTab, setActiveTab] = useState("1");
  const [isDirty, setIsDirty] = useState(false);
  const newReview = reviewData?.length === 0;
  const reviewId = reviewData?.[0]?.id;
  const newReviewId = addMode === false && reviewId ? reviewId : null;
  const reviewDate =
    segmentData?.find(
      (option) => option.value === editId && option.value !== "others"
    )?.label ||
    new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit" });

  const tabItems = useMemo(
    () => [
      {
        key: "1",
        label: isMobile ? "Diagnosis" : "Functional Diagnosis",
      },
      { key: "2", label: "Scales" },
    ],
    [isMobile]
  );

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const handleSubmit = async (values, flag) => {
    try {
      if (editId || newReviewId) {
        await updateSpeechReviewAPI(editId || newReviewId, values);
        setDailyReviewRefresh();
      } else {
        await speechReviewAPI(values);
        refreshPage();
        setAddMode(false);
        setDailyReviewRefresh();
      }
      message.success(`${flag} submitted successfully`);
      setIsDirty(false);
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const onDiagnosisSubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        functional_diagnosis: values,
        admission_id: params?.patient,
      },
      "Functional Diagnosis"
    );
  };

  const onScalesSubmit = (values) => {
    handleSubmit(
      {
        ...formData,
        scales: values,
        admission_id: params?.patient,
      },
      "Scales"
    );
  };

  const openEdit = async () => {
    if (editId && newReview === false) {
      try {
        const response = await getSpeechReviewAPI(editId);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error: ${error}`);
      }
    } else if (newReview === false) {
      try {
        const response = await getLatestSpeechReviewAPI(params?.patient);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error: ${error}`);
      }
    } else {
      try {
        const response = await getSpeechInspectionAPI(iAssessment);
        setFormData(response.data.data);
      } catch (error) {
        console.log(`Error:${error}`);
      }
    }
  };

  useEffect(() => {
    openEdit();
  }, [editId, dailyRefresh]);

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return diagnosisForm.submit();
      case "2":
        return scalesForm.submit();
      default:
        return "";
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      switch (activeTab) {
        case "1":
          diagnosisForm.validateFields().then((values) => {
            setIsDirty(false);
            onDiagnosisSubmit(values);
            setActiveTab(key);
          });
          break;
        case "2":
          scalesForm.validateFields().then((values) => {
            setIsDirty(false);
            onScalesSubmit(values);
            setActiveTab(key);
          });
          break;
        default:
          setActiveTab(key);
      }
    } else {
      setActiveTab(key);
    }
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
  };

  const handleCloseConfirm = () => {
    Modal.confirm({
      title: "Confirmation",
      content: "Are you sure you want to close?",
      onOk: () => {
        setVisible(false);
        setActiveSegment(reviewData[0]?.id);
      },
    });
  };

  const patientName = `${patientData?.admission?.patient?.first_name || ""} ${
    patientData?.admission?.patient?.last_name || ""
  }`.trim();

  return (
    <div>
      <Drawer
        styles={{
          body: {
            overflow: "hidden",
            paddingTop: 0,
            paddingBottom: 0,
            paddingInline: 24,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
        open={visible}
        width="100%"
        title={
          <div className="ia-drawer-header">
            <div className="ia-drawer-header__main">
              <LeftOutlined
                className="ia-drawer-header__back"
                onClick={handleCloseConfirm}
              />
              <div className="ia-drawer-header__identity">
                <div className="ia-drawer-header__name-row">
                  <Typography.Text
                    className="ia-drawer-header__name"
                    ellipsis={{ tooltip: patientName }}
                  >
                    {patientName}
                  </Typography.Text>
                  {patientData?.admission?.bed?.bed_no && (
                    <Tag color="orange" className="ia-drawer-header__bed">
                      <Image src={bedImg} preview={false} width={12} />{" "}
                      {patientData?.admission?.bed?.bed_no}
                    </Tag>
                  )}
                </div>
                <div className="ia-drawer-header__meta">
                  <Tag className="ia-drawer-header__tag">
                    {patientData?.admission?.patient?.gender}{" "}
                    {patientData?.admission?.patient?.age}
                  </Tag>
                  <Tag className="ia-drawer-header__tag">
                    IP ID: {patientData?.admission?.ip_id}
                  </Tag>
                  {reviewDate && (
                    <Tag className="ia-drawer-header__tag">{reviewDate}</Tag>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
        footer={null}
        onClose={() => {
          setVisible(false);
          setActiveSegment(reviewData[0]?.id);
        }}
        closeIcon={null}
        keyboard={false}
      >
        <div className="daily-review-form-shell">
          <div className="ia-form-tabs daily-review-form-tabs">
            <Row>
              <Col
                xs={24}
                sm={24}
                md={{ span: 23, offset: 1 }}
                lg={{ span: 21, offset: 3 }}
                xl={{ span: 19, offset: 5 }}
                xxl={{ span: 18, offset: 6 }}
              >
                <Tabs
                  activeKey={activeTab}
                  items={tabItems}
                  onChange={handleTabChange}
                />
              </Col>
            </Row>
          </div>

          <div className="ia-form-panel">
            <div className="ia-form-body" ref={formBodyRef}>
              {activeTab === "1" && (
                <Form
                  id="review-functional-diagnosis"
                  form={diagnosisForm}
                  layout="vertical"
                  onFinish={onDiagnosisSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms ip-forms-review"
                >
                  <SpeechSwallowInitialFunctionalDiagnosis
                    form={diagnosisForm}
                    context={SpeechReviewContext}
                  />
                </Form>
              )}
              {activeTab === "2" && (
                <Form
                  id="review-scales-form"
                  form={scalesForm}
                  layout="vertical"
                  onFinish={onScalesSubmit}
                  onValuesChange={onFormValuesChange}
                  initialValues={formData}
                  className="ip-forms ip-forms-review"
                >
                  <SpeechSwallowScalesReview form={scalesForm} />
                </Form>
              )}
            </div>

            <div className="ia-form-footer">
              {activeTab !== "2" && (
                <Button
                  onClick={() => {
                    getFormKey();
                    setVisible(false);
                  }}
                  style={{ marginRight: 8 }}
                >
                  Save & Close
                </Button>
              )}
              <Button
                onClick={() => {
                  getFormKey();
                  document.querySelector(".ip-forms")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                  const nextTab = (parseInt(activeTab) + 1).toString();
                  if (activeTab === "2") {
                    setVisible(false);
                  } else {
                    setActiveTab(nextTab);
                  }
                }}
                type="primary"
              >
                {activeTab === "2" ? "Done" : " Save & Proceed"}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default SpeechSwallowReviewForm;
